import { useSectionActions } from 'components/section-menu/SectionActionsContext';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { cn } from 'utils/cn';
import { getIdLink } from 'utils/linkUtil';

export type ContentMenuRow = {
    title: string;
    subtitles: string[];
    noTranslate?: boolean;
};

type Props = {
    rows: ContentMenuRow[];
    activeSection?: string;
    sectionErrors?: Record<string, boolean>;
    sectionHighlightCounts?: Record<string, number>;
    className?: string;
};

/** The left rule plus tint that marks the entry the page is scrolled to. */
const ACTIVE_ROW_CLASS = 'border-l-primary bg-primary/8 hover:bg-primary/8';
const ROW_CLASS =
    'text-primary flex items-center rounded-none border-l-2 border-l-transparent px-4 no-underline hover:bg-muted/50';

const ContentMenu = ({ rows, activeSection, sectionErrors, sectionHighlightCounts, className }: Props): JSX.Element => {
    const { requestExpandSection } = useSectionActions();
    const location = useLocation();

    return (
        <ul className={cn('m-0 list-none p-0', className)}>
            {rows.map((row, index) => {
                const sectionId = getIdLink(row.noTranslate ? row.title.toLocaleLowerCase() : row.title);
                const isActive = activeSection === sectionId;
                const hasError = sectionErrors?.[row.title] ?? false;
                const highlightCount = sectionHighlightCounts?.[row.title] ?? 0;

                return (
                    <React.Fragment key={`menu-group-${row.title}`}>
                        <li key={`${row.title}-list-item`} className={cn(index > 0 && 'pt-2')}>
                            <RouterLink
                                to={{ hash: `#${sectionId}` }}
                                state={location.state}
                                replace
                                onClick={() => requestExpandSection(row.title)}
                                className={cn(ROW_CLASS, 'h-10 font-semibold', isActive && ACTIVE_ROW_CLASS)}
                            >
                                <span className="flex-1">
                                    {row.noTranslate ? row.title : <FormattedMessage id={row.title} />}
                                </span>
                                {highlightCount > 0 && (
                                    <span className="bg-warning/15 text-fg-warning new:text-caption ml-2 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-[10px] px-1 text-[11px] font-bold">
                                        {highlightCount}
                                    </span>
                                )}
                                {hasError && <span className="bg-destructive ml-2 size-2 shrink-0 rounded-full" />}
                            </RouterLink>
                        </li>
                        {row.subtitles.map((subtitle) => {
                            const subSectionId = getIdLink(row.noTranslate ? subtitle.toLocaleLowerCase() : subtitle);
                            const isSubActive = activeSection === subSectionId;

                            return (
                                // Inset by a gutter and set in the 12px secondary type, so a sub-section reads as
                                // nested under its section.
                                <li key={`${subtitle}-list-item`} className="px-4">
                                    <RouterLink
                                        to={{ hash: `#${subSectionId}` }}
                                        state={location.state}
                                        replace
                                        onClick={() => requestExpandSection(row.title)}
                                        className={cn(
                                            ROW_CLASS,
                                            'text-fg-secondary py-1 text-xs/4.5 font-medium',
                                            isSubActive && ACTIVE_ROW_CLASS
                                        )}
                                    >
                                        {row.noTranslate ? subtitle : <FormattedMessage id={subtitle} />}
                                    </RouterLink>
                                </li>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </ul>
    );
};

export default ContentMenu;
